import { AiChatBaseDataProvider } from './AiChatBaseDataProvider';
import { Suggestion } from '../../../ui/gzTextareaWithSuggestions/types';
import {
  AiChatData,
  AiChatQuestion,
  DracoAIErrors,
  AiChatIncidentHistoryUpdate,
  AiChatItemData,
  AiChatStatus,
  IncidentHistoryUpdateTypes,
  AiChatQuestionsDetail,
  AiChatItemType,
} from '../types';
import { translateFn } from '../../../utils/translator.helper';
import { convertGridFiltersToExtJsFilters } from '../../../grid/utils/filters';
import { TimeRange, TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { GzChatCardState } from '../../gzChatCard/types';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../../../grid/gridView/types';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import moment from "moment-timezone";

export class AiChatNirvanaDataProvider extends AiChatBaseDataProvider {
  async getDataForView(): Promise<AiChatData> {
    const response = await this.dependencies.api.clients.readRecords(this.dependencies.incident.incidentId);
    let chatStatus = AiChatStatus.UNAVAILABLE;
    const incidentInEs = await this.dependencies.api.clients.checkIncidentInElasticSearch(
      this.dependencies.incident.insightId,
      this.dependencies.getActiveCompany().companyId
    );

    if (incidentInEs) {
      if (response.total) {
        switch (this.dependencies.incident.errorCode) {
          case DracoAIErrors.MAX_XRCA_SIZE_EXCEEDED:
            chatStatus = AiChatStatus.MAX_XRCA_SIZE_EXCEEDED_AFTER_UPDATE;
            break;

          case DracoAIErrors.XRCA_NOT_FOUND_IN_ES:
            chatStatus = AiChatStatus.XRCA_NOT_FOUND_IN_ES_AFTER_UPDATE;
            break;

          default:
            chatStatus = AiChatStatus.ACTIVE;
        }
      } else {
        chatStatus = AiChatStatus.EMPTY;
      }
    }

    let chatItems: Array<AiChatItemData> = response.data;
    // No need to retrieve incident history if chat has no messages
    if (chatItems?.[0]?.created) {
      const incidentHistoryUpdates = await this.getIncidentUpdates(chatItems[0].created);
      if (incidentHistoryUpdates?.length) {
        const updateMessages = this.convertIncidentUpdatesToMessages(incidentHistoryUpdates);
        updateMessages.forEach(element => {
          chatItems.push(element);
        });
        chatItems = this.sortChatItems(chatItems);
      }

      let chatMessageFound = false;
      while (!chatMessageFound && chatItems.length !== 0) {
        if (chatItems[0].type === AiChatItemType.CHAT_CARD) {
          chatMessageFound = true;
        } else {
          chatItems.shift();
        }
      }
    }

    const suggestions = await this.getSuggestions();
    let questionsDetail: AiChatQuestionsDetail = {
      totalNumberOfQuestions: 0,
      availableNumberOfQuestions: 0,
    };

    if (typeof this.dependencies.api.clients.getQuestionsDetail === 'function') {
      const detail = await this.dependencies.api.clients.getQuestionsDetail();
      if (detail) {
        questionsDetail = detail;
      }
    }

    return {
      status: chatStatus,
      chatItems: chatItems,
      defaultQuestionList: suggestions,
      totalNumberOfQuestions: questionsDetail.totalNumberOfQuestions,
      currentNumberOfQuestions: questionsDetail.availableNumberOfQuestions,
    };
  }

  private convertQuestionsToSuggestions(questions: Array<AiChatQuestion>): Array<Suggestion> {
    return questions.map((question): Suggestion => {
      return {
        id: question._id,
        text: question.text,
      };
    });
  }

  private async getSuggestions() {
    const pageNumber = DEFAULT_PAGE_NUMBER;
    const pageSize = DEFAULT_PAGE_SIZE;
    const sortColumnName = null;
    const sortDirection = null;
    const filters = [];
    const defaultQuestions = await this.dependencies.api.clients.readQuestions(
      pageNumber,
      pageSize,
      sortColumnName,
      sortDirection,
      filters
    );
    return this.convertQuestionsToSuggestions(defaultQuestions.data);
  }

  private async getIncidentUpdates(firstChatItemCreated: number) {
    const fromTimestampUtc = moment.tz(firstChatItemCreated, this.dependencies.getTimezone()).utc().toDate();
    let pageNumber = DEFAULT_PAGE_NUMBER;
    const pageSize = 100;
    const timeRange: TimeRange = {
      kind: TimeRangeKind.ABSOLUTE_TIME_RANGE,
      absolute: {
        fromTimestamp: fromTimestampUtc.getTime(),
        toTimestamp: new Date(Date.now()).getTime(),
      },
    };
    const filters = [
      {
        type: {
          in: [IncidentHistoryUpdateTypes.INCIDENT_UPDATED],
        },
      },
      {
        lastProcessed: {
          timeRange,
        },
      },
    ];
    const extJsFilters = convertGridFiltersToExtJsFilters(filters);
    try {
      const updates = await this.dependencies.api.clients.readXdrIncidentsHistory(
        pageNumber,
        pageSize,
        extJsFilters,
        this.dependencies.incident.incidentId
      );
      const incidentHistoryUpdates: Array<AiChatIncidentHistoryUpdate> = updates.data;
      // If there are more incident updates than the specified limit
      let valuesToLimitRatio = updates.total / pageSize;
      while (valuesToLimitRatio > 1) {
        pageNumber += 1;
        const nextUpdates = await this.dependencies.api.clients.readXdrIncidentsHistory(
          pageNumber,
          pageSize,
          extJsFilters,
          this.dependencies.incident.incidentId
        );
        if (nextUpdates.data.length) {
          nextUpdates.data.forEach((element: AiChatIncidentHistoryUpdate) => {
            incidentHistoryUpdates.push(element);
          });
        }
        valuesToLimitRatio -= 1;
      }
      return incidentHistoryUpdates;
    } catch (error) {
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: this.dependencies.translateFn('Failed to get incident history updates.'),
        severity: ToastSeverity.ERROR,
      });
    }
  }

  private convertIncidentUpdatesToMessages(
    incidentHistoryUpdates: Array<AiChatIncidentHistoryUpdate>
  ): Array<AiChatItemData> {
    return incidentHistoryUpdates.map((update: AiChatIncidentHistoryUpdate) => {
      const message = this.getUpdateMessageText(update);
      return {
        _id: update._id,
        type: update.type,
        state: GzChatCardState.SUCCESS,
        message,
        incident: {
          incidentId: update.incidentId,
        },
        created: update.lastProcessed,
      };
    });
  }

  private sortChatItems(unsortedChatItems: Array<AiChatItemData>): Array<AiChatItemData> {
    // Return the items in descending order
    return unsortedChatItems.sort(function (a, b) {
      if (a.created && b.created) {
        const createdDateB = new Date(b.created).getTime();
        const createdDateA = new Date(a.created).getTime();
        return createdDateA - createdDateB;
      } else {
        return 1;
      }
    });
  }

  private getUpdateMessageText(update: AiChatIncidentHistoryUpdate): string {
    let message = '';
    if (update && update.specifics) {
      if (update.specifics.newNodesCount && update.specifics.newAlertsCount) {
        message = translateFn(
          'Incident updated: {newNodesCount} new nodes, {newAlertsCount} new alerts. Incident status changed to Open. Previous responses may no longer be valid',
          {
            newNodesCount: update.specifics.newNodesCount,
            newAlertsCount: update.specifics.newAlertsCount,
          }
        );
      } else if (!update.specifics.newNodesCount && update.specifics.newAlertsCount) {
        message = translateFn(
          'Incident updated: {newAlertsCount} new alerts. Incident status changed to Open. Previous responses may no longer be valid',
          {
            newAlertsCount: update.specifics.newAlertsCount,
          }
        );
      } else if (update.specifics.newNodesCount && !update.specifics.newAlertsCount) {
        message = translateFn(
          'Incident updated: {newNodesCount} new nodes. Incident status changed to Open. Previous responses may no longer be valid',
          {
            newNodesCount: update.specifics.newNodesCount,
          }
        );
      } else {
        message = translateFn(
          'Incident updated. Incident status changed to Open. Previous responses may no longer be valid'
        );
      }
    }
    return message;
  }
}
